import type { GarmentState } from '../api/bookings';
import type { FeatureCode, Permission } from '../generated/enums';
import type { Hue } from '../vocabulary';
import { isOffTheBooking } from '../bookings/tones';
import { can, hasFeature } from '../session/capability';
import type { Session } from '../session/types';

/**
 * What a shop needs to know before it opens, and where each answer leads.
 *
 * <p>
 * The pure half of the Today screen, in the shape `pages/bookings/draft.ts` established. It holds
 * the card catalogue and the two rules that decide what a person sees; the screen holds the queries
 * and the markup.
 * </p>
 *
 * <p>
 * <strong>The screen itself is gated on nothing and every card is gated on its own module.</strong>
 * That is the whole arrangement rather than a detail: gating the screen on any one permission would
 * hand a 403 to somebody who holds the other four, which is the defect this screen was built to
 * end. A person with no rental grants simply sees fewer cards.
 * </p>
 *
 * <p>
 * <strong>Both axes, because the menu applies both.</strong> A card whose feature is outside the
 * plan is *hidden* here rather than shown locked — the menu shows a locked row so a customer
 * discovers a module exists and can buy it, and that argument is about the navigation, not about a
 * morning summary. A dead tile among live ones on the one screen read at a glance is noise.
 * </p>
 */
/**
 * The six the band uses, narrowed from the product's vocabulary in `components/hues.ts`.
 *
 * <p>
 * Narrowed rather than restated, so a hue that leaves the vocabulary is a compile error here rather
 * than a class name that quietly generates nothing. <strong>Per queue, not per module</strong>:
 * three of these cards open the same booking register on different saved views, so `moduleHue`
 * cannot answer for them — it would paint three of the six the same colour and the band would lose
 * the distinction it exists for.
 * </p>
 */
export type CardHue = Extract<Hue, 'info' | 'cyan' | 'purple' | 'indigo' | 'pink' | 'danger'>;

export interface TodayCard {
  id: string;
  /** The question the card answers, phrased as the thing being counted. */
  label: string;
  /** A Lucide class pair, matching the menu entry this card leads to. */
  icon: string;
  /**
   * The card's identity colour.
   *
   * <p>
   * One hue per card and, on this band, spent on the icon tile alone — see `TodayPage`, which
   * records why six equal tiles get one thing rather than the three the rule allows.
   * <strong>`danger` is not an identity here.</strong> It belongs to `overdue` because that
   * card *is* the late one, which is the single case where the reserved colour and the card's
   * subject are the same thing. Nothing else may take it, and no card takes `warning`: amber is
   * needs-attention everywhere in this product and is spoken for by the financial year in the
   * header.
   * </p>
   */
  hue: CardHue;
  requires: Permission;
  feature: FeatureCode;
  /** The menu key this card opens. */
  target: string;
  /** The register view to open it on, where the target has saved views. */
  targetView?: string;
  /** What the card says when the count is zero — an answer, never an apology. */
  empty: string;
  /**
   * What a flagged row says, and in which of the two reserved colours.
   *
   * <p>
   * Late and red where a card does not say, since that is what a flag has meant on this screen
   * since it was built. The pull-lists are why it is settable: a garment at the tailor is
   * needs-attention rather than late, and amber is the colour that means that everywhere in this
   * product. A card may not invent a third — the reservation is the point of both.
   * </p>
   */
  flag?: { label: string; tone: 'negative' | 'warning' };
}

/**
 * The five questions, in the order a counter asks them.
 *
 * <p>
 * Collections and returns first because they are today's promises; then what is late, which is
 * yesterday's; then the two queues that decide whether tomorrow's promises can be kept.
 * </p>
 *
 * <p>
 * <strong>Cleaning and alterations are two cards, not one "jobs due back".</strong> They carry
 * different permissions and different plan features — `JobKindAuthorizer` exists on the server for
 * exactly that reason — so a single card would have to be gated on one of them and would either
 * vanish for a shop that bought only laundry or show it work it cannot open.
 * </p>
 */
export const todayCards: readonly TodayCard[] = [
  {
    id: 'pickups',
    label: 'Collections today',
    icon: 'icon icon-package-check',
    hue: 'info',
    requires: 'RentalOrder:View',
    feature: 'RentalOrders',
    target: 'bookings',
    targetView: 'pickups',
    empty: 'Nothing to hand over today.',
  },
  {
    id: 'returns',
    label: 'Returns due today',
    icon: 'icon icon-package-open',
    hue: 'cyan',
    requires: 'RentalOrder:View',
    feature: 'RentalOrders',
    target: 'bookings',
    targetView: 'returns',
    empty: 'Nothing due back today.',
  },
  {
    id: 'overdue',
    label: 'Overdue',
    icon: 'icon icon-alarm-clock',
    hue: 'danger',
    requires: 'RentalOrder:View',
    feature: 'RentalOrders',
    target: 'bookings',
    targetView: 'overdue',
    empty: 'Nothing is late.',
  },
  {
    id: 'inspection',
    label: 'Awaiting inspection',
    icon: 'icon icon-search-check',
    hue: 'purple',
    requires: 'ReturnInspection:View',
    feature: 'ReturnInspection',
    target: 'returns',
    empty: 'Every return has been looked at.',
  },
  {
    id: 'cleaning',
    label: 'Cleaning due back',
    icon: 'icon icon-washing-machine',
    hue: 'indigo',
    requires: 'CleaningJob:View',
    feature: 'Laundry',
    target: 'cleaning',
    empty: 'Nothing is at the laundry.',
  },
  {
    id: 'alterations',
    label: 'Alterations due back',
    icon: 'icon icon-scissors',
    hue: 'pink',
    requires: 'AlterationJob:View',
    feature: 'Alterations',
    target: 'alterations',
    empty: 'Nothing is with a tailor.',
  },
];

/** The cards this person may see, in catalogue order. */
export function visibleCards(session: Session): TodayCard[] {
  return todayCards.filter(
    (card) => can(session, card.requires) && hasFeature(session, card.feature),
  );
}

/**
 * The two pull-lists: what leaves the shop tomorrow, and what comes back.
 *
 * <p>
 * <strong>Separate from `todayCards` because they are a different kind of answer.</strong> Those
 * six are counts of what is happening now and each opens a queue; these two are lists of
 * <em>garments</em>, read the evening before so a storeman can walk the rail and lay tomorrow's
 * stock out. In wedding season that preparation is the work rather than an optimisation, which is
 * why they are lists of things to pick up rather than a seventh and eighth number.
 * </p>
 *
 * <p>
 * Both gate on the register's own grant and feature, since both are the register read through a
 * date — the card is the summary and its tab is the rest of it.
 * </p>
 */
export const tomorrowLists: readonly TodayCard[] = [
  {
    id: 'pickupsTomorrow',
    label: 'Going out tomorrow',
    icon: 'icon icon-package-check',
    hue: 'info',
    requires: 'RentalOrder:View',
    feature: 'RentalOrders',
    target: 'bookings',
    targetView: 'pickupsTomorrow',
    empty: 'Nothing leaves the shop tomorrow.',
    flag: { label: 'At the tailor', tone: 'warning' },
  },
  {
    id: 'returnsTomorrow',
    label: 'Coming back tomorrow',
    icon: 'icon icon-package-open',
    hue: 'cyan',
    requires: 'RentalOrder:View',
    feature: 'RentalOrders',
    target: 'bookings',
    targetView: 'returnsTomorrow',
    empty: 'Nothing is due back tomorrow.',
  },
];

/** The pull-lists this person may see. Same two rules the cards use, and deliberately not a third. */
export function visibleTomorrowLists(session: Session): TodayCard[] {
  return tomorrowLists.filter(
    (list) => can(session, list.requires) && hasFeature(session, list.feature),
  );
}

/**
 * A window of calendar days, inclusive at both ends.
 *
 * <p>
 * The same convention rule 14 sets for every range in this product — "the 1st to the 7th" is seven
 * days — so the server's half-open translation of it stays an implementation detail of the query
 * rather than something a screen has to know.
 * </p>
 */
export interface DateRange {
  from: string;
  to: string;
}

/** The three windows the volume figures are counted over, all ending today. */
export interface TradingPeriods {
  today: DateRange;
  week: DateRange;
  month: DateRange;
}

/**
 * Today, the week so far, and the month so far.
 *
 * <p>
 * <strong>All three end today rather than running to the end of the period</strong>, which is what
 * makes them comparable to each other on the morning they are read: "47 this month" means 47 taken,
 * not 47 out of a month that has not happened. A window closing in the future would count the same
 * bookings and read as a target nobody set.
 * </p>
 *
 * <p>
 * <strong>The week starts on Monday, and that is a decision about this trade.</strong> Saturday and
 * Sunday are when a shop takes its bookings, so an ISO week keeps the weekend whole at the end of
 * the week it belongs to; the locale default of Sunday would split it across two figures and make
 * the busiest two days of the season look like two quiet halves. Computed by hand rather than
 * through `startOf('week')` for the same reason — that reads the locale, so the same screen would
 * count differently in two browsers.
 * </p>
 */
export function tradingPeriods(today: string): TradingPeriods {
  const parsed = new Date(`${today}T00:00:00Z`);

  // Monday-relative: `getUTCDay` is 0 on Sunday, so `(day + 6) % 7` is how many days back Monday is
  // — 0 on Monday and 6 on Sunday.
  const sinceMonday = (parsed.getUTCDay() + 6) % 7;

  const monday = new Date(parsed);
  monday.setUTCDate(monday.getUTCDate() - sinceMonday);

  const first = new Date(parsed);
  first.setUTCDate(1);

  const iso = (value: Date) => value.toISOString().slice(0, 10);

  return {
    today: { from: today, to: today },
    week: { from: iso(monday), to: today },
    month: { from: iso(first), to: today },
  };
}

/**
 * The three volume figures, in the order they are read.
 *
 * <p>
 * Widest last. A shop looks at today's number first and the month's for reassurance, and the
 * template sets a row of figures left to right in the order it wants them read.
 * </p>
 */
export const tradingPeriodLabels: readonly { id: keyof TradingPeriods; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
];

/**
 * One row of a list card.
 *
 * <p>
 * Deliberately flat strings rather than each card's own record type. The rows exist to let somebody
 * recognise a name before they decide to open the screen, and a shared shape is what lets one
 * component render all six cards — six row renderers would drift, and five of them would be the ones
 * nobody looks at.
 * </p>
 *
 * <p>
 * <strong>The four fields are the template's own row, and the order matters.</strong>
 * `salesDashboard.tsx` sets a list row as a leading tile, then the **name** in
 * `text-xs font-semibold` with the **reference** muted beneath it, then a right-hand column
 * carrying a figure over its own small label. An earlier version here put the reference *above*
 * the name — the `inventoryDashboard.tsx` arrangement — which reads as a code with a caption rather
 * than a thing with an identifier.
 * </p>
 */
export interface TodayRow {
  key: string;
  /** The thing itself, and what the eye lands on first. */
  primary: string;
  /** Its identifier, muted, under the name. The badge sits inline beside it. */
  secondary?: string;
  /**
   * The right-hand column: a short figure over a muted label saying what it counts.
   *
   * <p>
   * Short is a constraint rather than a preference — the template puts a number here and the column
   * is sized for one. Anything that can run long is truncated by the renderer rather than allowed
   * to squeeze the name.
   * </p>
   */
  trailing?: string;
  /**
   * The same column, when what goes in it is a `DateOnly` the renderer must format.
   *
   * <p>
   * Split from {@link trailing} rather than flagged, because the alternative is this module
   * importing `format/dates.ts` — which resolves the shop's *chosen* pattern at call time, from
   * runtime state. A pure module that read a preference would stop being testable without one.
   * Two fields, and the renderer knows which is which without being told.
   * </p>
   */
  trailingDate?: string;
  trailingLabel?: string;
  /**
   * A picture of the thing, where one exists — the tile the template fills with a product glyph.
   *
   * <p>
   * Null is ordinary and the renderer falls back to the card's own icon. A thumbnail is decoration,
   * so a display path must survive its absence: that is the rule `GarmentThumbnailResolver` exists
   * to keep on the server, and it holds here too.
   * </p>
   */
  image?: string | null;
  /** Set where the row itself is the late or awkward one, so it can carry the reserved colour. */
  flagged?: boolean;
}

/** How many rows a card lists before it stops and says how many more there are. */
export const rowLimit = 4;

/**
 * How many garments a pull-list shows, which is deliberately more than {@link rowLimit}.
 *
 * <p>
 * The queue cards list a few names so somebody can recognise the morning before opening the screen
 * that does the work. A pull-list <em>is</em> the work — it is read standing at the rail — so a
 * list that stopped at four would send a storeman to the register for the fifth garment of nine.
 * Eight covers an ordinary evening's preparation; a wedding party of twenty still overflows, and
 * says so.
 * </p>
 */
export const pullLimit = 8;

/** What the card prints under its last row, or null when it listed everything. */
export function moreLabel(count: number, shown: number): string | null {
  return count > shown ? `and ${count - shown} more` : null;
}

/**
 * Rows for a booking-shaped card.
 *
 * <p>
 * Takes only what it needs rather than the `Booking` type, so it is testable without building one
 * and cannot quietly start depending on a field the register happens to carry.
 * </p>
 */
export function bookingRows(
  bookings: readonly {
    id: number;
    documentNumber: string;
    quotationNumber: string | null;
    customerName: string;
    garmentCount?: number;
  }[],
  options: { flagged?: boolean } = {},
): TodayRow[] {
  return bookings.slice(0, rowLimit).map((booking) => ({
    key: String(booking.id),
    primary: booking.customerName,
    // The reference a customer reads off their copy. A hold carries neither number, and printing
    // an empty string beside a name reads as a rendering fault rather than as "not numbered yet".
    secondary: booking.documentNumber || booking.quotationNumber || 'Not numbered yet',
    // What the trailing column is for: the size of the job behind the name. "Patel · 3 garments"
    // is the difference between a counter reaching for one hanger and clearing a rail.
    ...(booking.garmentCount === undefined
      ? {}
      : {
          trailing: String(booking.garmentCount),
          trailingLabel: booking.garmentCount === 1 ? 'garment' : 'garments',
        }),
    ...(options.flagged ? { flagged: true } : {}),
  }));
}

/**
 * Which garments a pull-list is about.
 *
 * <p>
 * `out` is tomorrow's collections — everything not yet handed over. `back` is tomorrow's returns,
 * which is <strong>only what is physically `Issued`</strong>: a booking's possession ends tomorrow
 * whatever state its lines are in, so counting the rest would put garments already sitting on the
 * inspection bench on a list of things to expect through the door.
 * </p>
 */
export type PullDirection = 'out' | 'back';

/**
 * The garments to lay out, from the bookings whose possession starts or ends that day.
 *
 * <p>
 * <strong>Garments rather than bookings, which is the whole point of these two lists.</strong> A
 * row of customer names tells a storeman how many people to expect and nothing about what to take
 * off the rail; the serial and the design are what the job needs.
 * </p>
 *
 * <p>
 * <strong>A garment at the tailor is listed and flagged, never dropped.</strong> "Not on the rail
 * because it is being altered" is the single most useful thing this list can say the evening
 * before — it is the one garment somebody has to chase — and a list that silently omitted it would
 * read as a shorter, easier evening than the shop is actually having. Same distinction the scoped
 * scan box draws between "at the tailor" and "not on this booking".
 * </p>
 *
 * <p>
 * Tombstones are excluded through the shared predicate rather than by naming the two states here,
 * for the reason that predicate exists: a removed garment is still returned by the API, and every
 * reader meaning "the garments this booking is for" has to ask.
 * </p>
 */
export function garmentRows(
  bookings: readonly {
    customerName: string;
    wearers: readonly {
      garments: readonly {
        id: number;
        serialCode: string;
        itemName: string;
        imageUrl: string | null;
        state: GarmentState;
      }[];
    }[];
  }[],
  direction: PullDirection,
): TodayRow[] {
  const rows: TodayRow[] = [];

  for (const booking of bookings) {
    for (const wearer of booking.wearers) {
      for (const garment of wearer.garments) {
        if (isOffTheBooking(garment.state)) {
          continue;
        }

        if (direction === 'back' ? garment.state !== 'Issued' : garment.state === 'Issued') {
          continue;
        }

        rows.push({
          key: String(garment.id),
          /*
            The design over the serial and the customer, which is both how the eye searches a rail
            — a navy tuxedo first, the tag confirmed afterwards — and how the inspection card
            beside it already sets a garment.

            <strong>The variant is deliberately not on this line.</strong> It was, and driving it
            showed why not: "Navy Three-Piece Tuxedo · Size 36 / Colour Navy / Fit Slim fit" is
            twice what a third-width card holds, so every row with a badge on it truncated
            mid-word. The serial spells the same three axes out — `TUX_NAVY-36-NAVY-SLIM-001` — so
            nothing was lost by dropping it, which is the only reason dropping it was the fix.
          */
          primary: garment.itemName,
          secondary: garment.serialCode,
          /*
            Whose it is, on the right, where the template puts the figure.

            The customer moved off the reference line when the row gained its trailing column, and
            it belongs here: what to take down is the left of the row and who it is for is the
            right, which is the order somebody walking a rail actually needs them in.
          */
          trailing: booking.customerName,
          trailingLabel: direction === 'back' ? 'Returning' : 'Collecting',
          // A picture of the garment is worth more on this list than on any other screen in the
          // product — it is read while looking at the rail rather than at the screen.
          image: garment.imageUrl,
          ...(garment.state === 'Alteration' ? { flagged: true } : {}),
        });
      }
    }
  }

  return rows;
}

/** Rows for the inspection queue. */
export function inspectionRows(
  pending: readonly {
    rentalOrderItemId: number;
    garmentName: string;
    serialCode: string;
    bookingNumber?: string | null;
  }[],
): TodayRow[] {
  return pending.slice(0, rowLimit).map((item) => ({
    key: String(item.rentalOrderItemId),
    primary: item.garmentName,
    secondary: item.serialCode,
    // The booking moves to the trailing column rather than sharing the reference line, so the two
    // codes on this row stop reading as one long code with a separator in it.
    ...(item.bookingNumber ? { trailing: item.bookingNumber, trailingLabel: 'Booking' } : {}),
  }));
}

/**
 * Rows for a job queue, and the one place this screen computes severity of its own.
 *
 * <p>
 * A job whose due date has passed is flagged, because a queue of six that contains one late job and
 * a queue of six that does not are different mornings. `today` is passed in rather than read here,
 * the rule `registerViews` already follows: a laptop with a wrong clock must not colour a job red.
 * </p>
 */
export function jobRows(
  jobs: readonly {
    id: number;
    garmentName: string;
    serialCode: string;
    dueOn: string | null;
    vendorName: string | null;
  }[],
  today: string,
): TodayRow[] {
  return jobs.slice(0, rowLimit).map((job) => ({
    key: String(job.id),
    primary: job.garmentName,
    secondary: job.vendorName ? `${job.serialCode} · ${job.vendorName}` : job.serialCode,
    // The due date is the trailing figure, which is what this queue is actually about — the vendor
    // stays on the reference line because it is who, and a queue is read for when.
    ...(job.dueOn === null ? {} : { trailingDate: job.dueOn, trailingLabel: 'Due' }),
    ...(job.dueOn !== null && job.dueOn < today ? { flagged: true } : {}),
  }));
}

/**
 * Jobs still out — the ones a morning card is about.
 *
 * <p>
 * `useGarmentJobs` is asked without closed jobs, so this is a narrowing rather than a filter the
 * server forgot: a job that has come back but has not been completed is still work in hand, and one
 * already completed is not. Counting the raw list would make a card that never falls to zero.
 * </p>
 */
export function outstandingJobs<T extends { completedOn: string | null }>(jobs: readonly T[]): T[] {
  return jobs.filter((job) => job.completedOn === null);
}

/* ------------------------------------------------------------------- money */

/**
 * The money panels, and what each one costs to see.
 *
 * <p>
 * <strong>Two entries rather than one "money" card</strong>, for the reason cleaning and
 * alterations are two: they carry different grants. `/api/cash` is the second place in the product
 * that enforces `ViewFinancials`, deliberately — a counter clerk may see the shop's work without
 * seeing its cash position — while an expense voucher is an ordinary `View`. A single card would
 * have to be gated on one of them, and would either vanish for a bookkeeper who may read vouchers
 * but not the drawer, or show a clerk a figure the API will refuse.
 * </p>
 *
 * <p>
 * They share `CashAndExpense` and the `success` hue, which is what `moduleHue` already says both
 * screens own — so the pair reads as one region of the morning without being one card.
 * </p>
 */
export interface MoneyPanel {
  id: 'cash' | 'expenses' | 'outstanding';
  title: string;
  subtitle: string;
  requires: Permission;
  feature: FeatureCode;
  /** The menu key the panel's button opens. */
  target: string;
  /**
   * What the button says.
   *
   * <p>
   * Named per panel rather than the list card's "View all", which reads as a truncated list here
   * and there is no list: the cash panel is three figures and its screen is a book of movements.
   * Naming the destination is also what keeps the button from restating the title above it.
   * </p>
   */
  action: string;
}

export const moneyPanels: readonly MoneyPanel[] = [
  {
    id: 'cash',
    title: 'Cash & bank',
    subtitle: 'What is in it, and what moved today',
    requires: 'CashAndBank:ViewFinancials',
    feature: 'CashAndExpense',
    target: 'cash',
    action: 'Cash book',
  },
  {
    id: 'expenses',
    title: 'Expenses',
    subtitle: 'Vouchers recorded, not money owed',
    requires: 'Expense:View',
    feature: 'CashAndExpense',
    target: 'expenses',
    action: 'All vouchers',
  },
  {
    // Amounts, and only amounts — so ViewFinancials, the same as the screen it opens.
    id: 'outstanding',
    title: 'Outstanding',
    subtitle: 'Owed to the shop, and owed by it',
    requires: 'Outstanding:ViewFinancials',
    feature: 'CashAndExpense',
    target: 'outstanding',
    action: 'Outstanding',
  },
];

/** The money panels this person may see. The same two rules the cards use, and not a third. */
export function visibleMoneyPanels(session: Session): MoneyPanel[] {
  return moneyPanels.filter(
    (panel) => can(session, panel.requires) && hasFeature(session, panel.feature),
  );
}

/** What crossed the drawer in the window the position was asked for. */
export interface MoneyMovement {
  moneyIn: number;
  moneyOut: number;
}

/**
 * Today's movement, summed across every account the branch holds.
 *
 * <p>
 * <strong>Summed from the position endpoint's own per-account figures, never from a page of ledger
 * rows.</strong> The movements list is paged at 25, so adding it up would report a quiet day
 * correctly and a busy one short — which is the defect the invoice register's totals are a separate
 * read to avoid. These figures come back already netted server-side over the whole window.
 * </p>
 *
 * <p>
 * <strong>A retired account is included</strong>, for the reason it stays in the total on the cash
 * screen: it takes no new postings, but what moved through it today still moved.
 * </p>
 *
 * <p>
 * <strong>Money moved between the shop's own accounts is left out</strong> (Cash & bank step 3): the
 * evening's deposit is out of the till and into the bank, and counting both would report the
 * takings twice and the same sum again as spending.
 * </p>
 */
export function movementIn(
  accounts:
    | readonly { in: number; out: number; transferredIn?: number; transferredOut?: number }[]
    | undefined,
): MoneyMovement {
  return {
    moneyIn: (accounts ?? []).reduce(
      (running, account) => running + account.in - (account.transferredIn ?? 0),
      0,
    ),
    moneyOut: (accounts ?? []).reduce(
      (running, account) => running + account.out - (account.transferredOut ?? 0),
      0,
    ),
  };
}
